import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWithcookieData, selectWithcookieData } from '../../../redux/WithcookieSlice';
import { LogicComponentData, ComponentRenderMode } from '../../../types/logic';
import type { BannerItem } from './MainBanner.types';

interface MainBannerState {
  currentIndex: number;
  isTransitioning: boolean;
  isPaused: boolean;
  touchStartX: number | null;
  touchEndX: number | null;
  banners: BannerItem[];
  isLoading: boolean;
  isVideoPlaying: boolean;
  isHovering: boolean;
}

/**
 * 배너 이미지 소스 URL을 가져오는 함수
 */
const getImageSource = (banner: BannerItem, index: number): string => {
  if (!banner.icon) {
    return 'https://via.placeholder.com/1200x400';
  }
  
  // S3 URL 패턴 확인
  if (typeof banner.icon === 'string' && banner.icon.includes('amazonaws.com')) {
    return banner.icon;
  }
  
  // 상대 경로인 경우 기본 URL 추가
  if (typeof banner.icon === 'string' && banner.icon.startsWith('/')) {
    return `${window.location.origin}${banner.icon}`;
  }
  
  return banner.icon as string;
};

/**
 * 레거시 위치 속성을 새로운 위치 값으로 변환하는 함수
 */
const getPositionFromLegacy = (horizontal = 'center', vertical = 'center'): string => {
  const hPos = horizontal.toLowerCase();
  const vPos = vertical.toLowerCase();
  
  if (vPos === 'top') {
    if (hPos === 'left') return '1';
    if (hPos === 'center') return '2';
    if (hPos === 'right') return '3';
  }
  else if (vPos === 'center' || vPos === 'middle') {
    if (hPos === 'left') return '4';
    if (hPos === 'center') return '5';
    if (hPos === 'right') return '6';
  }
  else if (vPos === 'bottom') {
    if (hPos === 'left') return '7';
    if (hPos === 'center') return '8';
    if (hPos === 'right') return '9';
  }
  
  return '5'; // 기본값: 가운데 중앙
};

/**
 * 배너 데이터에 속성 설정 및 처리
 */
const processInitialBanner = (banner: BannerItem, props: any): BannerItem => {
  return {
    ...banner,
    showTitle: banner.showTitle !== undefined ? banner.showTitle : true,
    showButton: banner.showButton !== undefined ? banner.showButton : true,
    position: banner.position || '5',
    ...((!banner.position && (banner.horizontalPosition || banner.verticalPosition)) && {
      position: getPositionFromLegacy(banner.horizontalPosition, banner.verticalPosition)
    }),
    textColor: banner.textColor || 'white',
    textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)',
    buttonBgColor: banner.buttonBgColor || '#ff6b6b',
    buttonTextColor: banner.buttonTextColor || 'white',
    buttonHoverColor: banner.buttonHoverColor || '#ff5252',
    buttonText: banner.buttonText || '자세히 보기',
    transparentButton: banner.transparentButton !== undefined ? banner.transparentButton : false,
    buttonWidth: banner.buttonWidth || '300px',
    buttonHeight: banner.buttonHeight || 'auto',
    mobileButtonWidth: banner.mobileButtonWidth || 'auto',
    mobileButtonHeight: banner.mobileButtonHeight || 'auto',
    mobileFullWidth: banner.mobileFullWidth || false,
    url: banner.url || '#',
    text: banner.text || '',
    description: banner.description || '',
    videoUrl: banner.videoUrl || '',
    mediaType: banner.mediaType || (banner.videoUrl ? 'video' : 'image'),
    autoplay: banner.autoplay !== undefined ? banner.autoplay : true,
    muted: banner.muted !== undefined ? banner.muted : true,
    loop: banner.loop !== undefined ? banner.loop : true,
    hasBackground: banner.hasBackground !== undefined ? banner.hasBackground : false
  };
};

/**
 * MainBanner 컴포넌트의 비즈니스 로직을 담당하는 커스텀 훅
 */
export const useMainBannerLogic = (componentData: LogicComponentData, mode: ComponentRenderMode) => {
  const { props: componentProps = {} } = componentData;
  const viewMode = componentProps.viewMode || 'pc';
  const dispatch = useDispatch() as any;
  const withcookieData = useSelector(selectWithcookieData);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const initialDataLoaded = useRef(false);
  
  // 상태 관리
  const [state, setState] = useState<MainBannerState>({
    currentIndex: 0,
    isTransitioning: false,
    isPaused: false,
    touchStartX: null,
    touchEndX: null,
    banners: [],
    isLoading: true,
    isVideoPlaying: false,
    isHovering: false
  });

  const isMobile = viewMode === 'mobile' || viewMode === 'tablet' || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // 배너 설정값
  const bannerMode = componentProps.mode || 'slider';
  const autoPlay = componentProps.autoPlay !== false;
  const autoPlaySpeed = componentProps.autoPlaySpeed || 5000;
  const transitionSpeed = componentProps.transitionSpeed || 500;
  const showDots = componentProps.showDots !== false;
  const showArrows = componentProps.showArrows !== false;
  const pauseOnHover = componentProps.pauseOnHover !== false;
  const infiniteLoop = componentProps.infiniteLoop !== false;
  const mobileHeight = componentProps.mobileHeight || '400px';
  const desktopHeight = componentProps.desktopHeight || '600px';

  // Redux 데이터 로드
  useEffect(() => {
    if (!withcookieData) {
      dispatch(fetchWithcookieData())
        .catch((error: any) => {
          console.error('배너 데이터 로딩 실패:', error);
          setState(prev => ({ ...prev, isLoading: false }));
        });
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [dispatch, withcookieData]);

  // 배너 데이터 처리
  useEffect(() => {
    try {
      let bannerData = null;
      
      // deviceProperty 확인
      if (componentProps?.deviceProperty) {
        const deviceType = viewMode === 'mobile' || viewMode === 'tablet' ? 'mobile' : 'pc';
        if (componentProps.deviceProperty[deviceType]?.banners && 
            Array.isArray(componentProps.deviceProperty[deviceType].banners)) {
          bannerData = JSON.parse(JSON.stringify(componentProps.deviceProperty[deviceType].banners));
        }
      }
      
      // 다양한 위치에서 배너 데이터 찾기
      if (!bannerData && componentProps?.banners && Array.isArray(componentProps.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.banners));
      } else if (!bannerData && componentProps?.data?.banners && Array.isArray(componentProps.data.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.data.banners));
      } else if (!bannerData && componentProps?.content?.banners && Array.isArray(componentProps.content.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.content.banners));
      } else if (!bannerData && componentData?.content?.banners && Array.isArray(componentData.content.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentData.content.banners));
      }
      
      if (bannerData && bannerData.length > 0) {
        const processedData = bannerData.map((banner: BannerItem) => processInitialBanner(banner, componentProps));
        setState(prev => ({ ...prev, banners: processedData, isLoading: false }));
        initialDataLoaded.current = true;
      } else {
        setState(prev => ({ ...prev, banners: [], isLoading: false }));
        initialDataLoaded.current = true;
      }
    } catch (error) {
      console.error('[배너] 데이터 처리 중 오류:', error);
      setState(prev => ({ ...prev, banners: [], isLoading: false }));
      initialDataLoaded.current = true;
    }
  }, [componentProps, viewMode, componentData]);

  // 다음 슬라이드로 이동
  const goToNext = useCallback(() => {
    if (state.isTransitioning || state.banners.length === 0) return;
    
    setState(prev => ({
      ...prev,
      currentIndex: infiniteLoop || prev.currentIndex < prev.banners.length - 1 
        ? (prev.currentIndex + 1) % prev.banners.length 
        : prev.currentIndex,
      isTransitioning: true
    }));

    setTimeout(() => {
      setState(prev => ({ ...prev, isTransitioning: false }));
    }, transitionSpeed);
  }, [state.isTransitioning, state.banners.length, infiniteLoop, transitionSpeed]);

  // 이전 슬라이드로 이동
  const goToPrev = useCallback(() => {
    if (state.isTransitioning || state.banners.length === 0) return;
    
    setState(prev => ({
      ...prev,
      currentIndex: infiniteLoop || prev.currentIndex > 0
        ? prev.currentIndex === 0 ? prev.banners.length - 1 : prev.currentIndex - 1
        : prev.currentIndex,
      isTransitioning: true
    }));

    setTimeout(() => {
      setState(prev => ({ ...prev, isTransitioning: false }));
    }, transitionSpeed);
  }, [state.isTransitioning, state.banners.length, infiniteLoop, transitionSpeed]);

  // 특정 슬라이드로 이동
  const goToSlide = useCallback((index: number) => {
    if (state.isTransitioning || index === state.currentIndex || state.banners.length === 0) return;
    
    setState(prev => ({
      ...prev,
      currentIndex: index,
      isTransitioning: true
    }));

    setTimeout(() => {
      setState(prev => ({ ...prev, isTransitioning: false }));
    }, transitionSpeed);
  }, [state.isTransitioning, state.currentIndex, state.banners.length, transitionSpeed]);

  // 자동 재생
  useEffect(() => {
    if (!autoPlay || state.isPaused || mode === 'editor' || state.banners.length <= 1) {
      return;
    }

    // 현재 배너가 비디오인지 확인
    const currentBanner = state.banners[state.currentIndex];
    const isCurrentBannerVideo = currentBanner?.mediaType === 'video' && currentBanner?.videoUrl;
    
    // 비디오가 아닌 경우에만 타이머 설정
    if (!isCurrentBannerVideo) {
      intervalRef.current = setInterval(() => {
        goToNext();
      }, autoPlaySpeed);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoPlay, autoPlaySpeed, state.isPaused, mode, state.banners, state.currentIndex, goToNext]);

  // 마우스 호버 시 일시정지
  const handleMouseEnter = useCallback(() => {
    setState(prev => ({ ...prev, isHovering: true }));
    if (pauseOnHover) {
      setState(prev => ({ ...prev, isPaused: true }));
    }
  }, [pauseOnHover]);

  const handleMouseLeave = useCallback(() => {
    setState(prev => ({ ...prev, isHovering: false }));
    if (pauseOnHover) {
      setState(prev => ({ ...prev, isPaused: false }));
    }
  }, [pauseOnHover]);

  // 터치 이벤트 핸들러
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setState(prev => ({ ...prev, touchStartX: e.touches[0].clientX }));
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setState(prev => ({ ...prev, touchEndX: e.touches[0].clientX }));
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!state.touchStartX || !state.touchEndX) return;
    
    const diff = state.touchStartX - state.touchEndX;
    const threshold = 50; // 스와이프 감도

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }

    setState(prev => ({ 
      ...prev, 
      touchStartX: null, 
      touchEndX: null 
    }));
  }, [state.touchStartX, state.touchEndX, goToNext, goToPrev]);

  // 배너 클릭 핸들러
  const handleBannerClick = useCallback((banner: BannerItem) => {
    if (mode === 'editor' || !banner.url || banner.url === '#') {
      return;
    }
    
    // 외부 링크 처리
    if (banner.url.startsWith('http') || banner.url.startsWith('//')) {
      window.open(banner.url, '_blank');
    } else {
      window.location.href = banner.url;
    }
  }, [mode]);

  // 비디오 관련 핸들러
  const handleVideoClick = useCallback(() => {
    setState(prev => ({ ...prev, isVideoPlaying: !prev.isVideoPlaying }));
  }, []);

  const handleVideoEnded = useCallback(() => {
    setState(prev => ({ ...prev, isVideoPlaying: false }));
    goToNext();
  }, [goToNext]);

  const handleVideoPlaying = useCallback(() => {
    setState(prev => ({ ...prev, isVideoPlaying: true }));
  }, []);

  const handleVideoError = useCallback((error: any) => {
    console.error('비디오 로드 오류:', error);
  }, []);

  // 반응형 높이 계산
  const getContainerHeight = useCallback(() => {
    if (typeof window === 'undefined') return desktopHeight;
    
    const isMobileDevice = window.innerWidth <= 768;
    return isMobileDevice ? mobileHeight : desktopHeight;
  }, [mobileHeight, desktopHeight]);

  // props 변경 감지 및 적용
  useEffect(() => {
    if (!componentProps) return;
    
    setState(prev => {
      let newBanners = [...prev.banners];
      let needsUpdate = false;
      
      // 배너 추가 감지
      if (componentProps.addBanner) {
        const newBanner = processInitialBanner(componentProps.addBanner, componentProps);
        newBanners = [...newBanners, newBanner];
        needsUpdate = true;
      }
      
      // 배너 업데이트 감지
      if (componentProps.updateBanner && componentProps.updateIndex !== undefined) {
        const updateIndex = componentProps.updateIndex;
        if (updateIndex >= 0 && updateIndex < newBanners.length) {
          newBanners[updateIndex] = {
            ...newBanners[updateIndex],
            ...componentProps.updateBanner
          };
          needsUpdate = true;
        }
      }
      
      // 전체 배너 업데이트
      if (componentProps.updatedBanners && Array.isArray(componentProps.updatedBanners)) {
        newBanners = componentProps.updatedBanners.map(banner => processInitialBanner(banner, componentProps));
        needsUpdate = true;
      }
      
      return needsUpdate ? { ...prev, banners: newBanners } : prev;
    });
  }, [componentProps]);

  // 비디오 ref 설정 함수
  const setVideoRef = useCallback((ref: HTMLVideoElement | null) => {
    videoRef.current = ref;
  }, []);

  return {
    // 데이터를 ComponentSkinWrapper가 기대하는 구조로 반환
    data: {
      // 상태
      currentIndex: state.currentIndex,
      isTransitioning: state.isTransitioning,
      isPaused: state.isPaused,
      isLoading: state.isLoading,
      isVideoPlaying: state.isVideoPlaying,
      isHovering: state.isHovering,
      
      // 배너 데이터
      banners: state.banners,
      
      // 설정
      bannerMode,
      autoPlay,
      autoPlaySpeed,
      transitionSpeed,
      showDots,
      showArrows,
      pauseOnHover,
      infiniteLoop,
      containerHeight: getContainerHeight(),
      isMobile,
      
      // 유틸리티
      isEditorMode: mode === 'editor',
      isPreviewMode: mode === 'preview',
      getImageSource,
      videoRef: videoRef.current,
      
      // componentProps로 전달된 props
      props: {
        style: componentProps.style || {},
        className: componentProps.className || ''
      }
    },
    
    // 액션
    actions: {
      goToNext,
      goToPrev,
      goToSlide,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onBannerClick: handleBannerClick,
      onVideoClick: handleVideoClick,
      onVideoEnded: handleVideoEnded,
      onVideoPlaying: handleVideoPlaying,
      onVideoError: handleVideoError,
      setVideoRef
    }
  };
};